import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Divider from '@mui/material/Divider';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import {
  PatientDTO,
  ServiceFacilityDTO,
  UserDTO,
  NPISearchItemDTO,
  InsuranceDTO,
} from 'dtos';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import Controller from 'core/components/FormController';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  NotifyProps,
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  appointmentsEventChannel,
  patientEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  normalizeUser,
  validatePatient,
} from 'pages/Dashboard/utils/helper';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import ServiceFacilitySelect from 'pages/Dashboard/components/ServiceFacilitySelect';
import ReferringProvider from 'pages/Dashboard/components/ReferringProvider';
import {
  useAppointmentReferringProviderSuggestions,
} from 'pages/Dashboard/components/ReferringProvider/hooks';
import Select from 'core/components/Select';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import DateTimeIntervalPicker from 'core/components/DateTimePicker/DateTimeIntervalPicker';
import {
  MIN_APPOINTMENT_DATE_ERROR_MESSAGE,
  validateDateTimeIntervalValue,
  validateMinAppointmentDateTime,
} from 'core/components/DateTimePicker/dateTimeInterval';
import EndsOnConditionFields from 'pages/Dashboard/pages/Appointments/pages/List/components/EndsOnConditionFields';
import RecurrenceCheckbox from 'pages/Dashboard/pages/Appointments/pages/List/components/RecurrenceCheckbox';
import RecurrenceFields from 'pages/Dashboard/pages/Appointments/pages/List/components/RecurrenceFields';
import {
  CreateAppointmentRequestResponseDataType,
  ModifyAppointmentRequestResponseDataType,
  useCreateAppointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  CreateEventFormData,
  EventType,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  DEFAULT_APPOINTMENT_DURATION,
  getDefaultEventFormData,
  serializeAppointmentFormData,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  useColorsHashMap,
  useEncounterSettings,
} from 'pages/Dashboard/pages/Encounters/hooks';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import ButtonCheckbox from 'pages/Dashboard/components/ButtonCheckbox';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import HomeIcon from 'core/icons/home';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import ProviderLookup from 'views/EMR/components/ProviderLookup';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import TelehealthVisitTypeIcon from 'pages/Dashboard/components/TelehealthVisitTypeIcon';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';
import {
  useGetPatientInsurances,
} from 'views/EMR/components/Insurance/hooks';
import mapNPISearchItemToProviderEntry from 'pages/Dashboard/utils/providers';
import {
  DEFAULT_DURATION_LIST,
} from 'core/components/DateTimePicker/DurationPicker/utils';
import {
  hasTelehealthPatientContact,
} from 'utils/telehealth';

export type CreateAppointmentDialogParams = {
  defaultStartDateTime?: Dayjs;
  defaultDurationInMinutes?: number;
  defaultDoctor?: UserDTO;
  defaultServiceFacility?: ServiceFacilityDTO;
  defaultEventType?: number;
  defaultPatient?: Partial<PatientDTO>;
  defaultReason?: string;
  onAppointmentCreated?: () => void | Promise<void>;
};

type Props = CreateAppointmentDialogParams & {
  onClose: () => void;
  open: boolean;
};

type TitleProps = {
  active: number;
  onChange: (index: number) => void;
};

type AppointmentData = CreateAppointmentRequestResponseDataType |
  ModifyAppointmentRequestResponseDataType;

type AppointmentWarningData = {
  telehealthMeetingWarning?: string | null;
  isAppointmentOutsideAvailability?: boolean | null;
  isAppointmentInBlockedTimeSlot?: boolean | null;
};

export const appointmentOptions = [
  { label: 'Appointment', key: 'appointment', Icon: HomeIcon },
  { label: 'Time Block', key: 'timeBlock', Icon: EventBusyIcon },
  { label: 'Availability', key: 'availability', Icon: EventAvailableIcon },
];

const getWarningMessage = (appointment: AppointmentWarningData) => {
  const {
    telehealthMeetingWarning,
    isAppointmentOutsideAvailability,
    isAppointmentInBlockedTimeSlot,
  } = appointment;

  if (notEmptyString(telehealthMeetingWarning)) {
    return telehealthMeetingWarning ?? null;
  }

  if (isAppointmentInBlockedTimeSlot) {
    return 'Appointment saved during blocked time.';
  }

  return isAppointmentOutsideAvailability
    ? 'Appointment saved outside of provider availability.' : null;
};

export const handleAppointmentActionDialog = (
  appointment: AppointmentData,
  notify: (props: NotifyProps) => void,
  successMessage: string,
) => {
  const warningMessage = getWarningMessage(appointment);

  const message = isNil(warningMessage)
    ? successMessage : warningMessage;

  notify({
    message,
    variant: isNil(warningMessage)
      ? 'success' : 'warning',
  });
};

function Title({
  active,
  onChange,
}: TitleProps) {
  const caption = appointmentOptions[active].label;
  return (
    <div className="flex flex-1 flex-col space-y-2">
      <h2 className="text-lg font-semibold">
        Create
        {' '}
        {caption}
      </h2>
      <NavigationTabs
        className="flex flex-row"
        tabs={appointmentOptions}
        activeIndex={active}
        onClick={onChange}
      />
    </div>
  );
}

const defaultInsurance = Object.freeze({
  insuranceName: 'Not Set',
  insuranceId: 0,
});

export default function CreateAppointmentDialog({
  open,
  onClose: _onClose,
  defaultStartDateTime,
  defaultDurationInMinutes,
  defaultDoctor,
  defaultServiceFacility,
  defaultEventType = 0,
  defaultPatient,
  defaultReason,
  onAppointmentCreated,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;

  const [appointmentType, setAppointmentType] = useState<number>(defaultEventType);
  const isAppointment = appointmentType === 0;
  const isAvailability = appointmentType === 2;
  const isTimeBlock = appointmentType === 1;
  const notify = useNotify();
  const mixpanelTrack = useMixpanelTrack();
  const userInfo = useUserInfo();

  const currentDoctor = useMemo(
    () => {
      const { user } = userInfo ?? {};
      return user?.designation === 'Doctor' ? normalizeUser(user ?? {}, user?.userId) : null;
    },
    [userInfo],
  );

  const serviceFacilities = useServiceFacilities();

  const [defaultOrganizationServiceFacility] = serviceFacilities ?? [];

  const formProps = useForm<CreateEventFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: getDefaultEventFormData({
      defaultStartDateTime: defaultStartDateTime ?? dayjs().tz(),
      defaultDurationInMinutes,
      doctor: defaultDoctor ?? currentDoctor,
      serviceFacility: defaultServiceFacility ?? defaultOrganizationServiceFacility,
      patient: defaultPatient,
      reason: defaultReason,
    }),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    trigger,
    watch,
    getValues,
    setError,
    formState: { isValid, isSubmitting },
  } = formProps;

  const [eventDateTimeInputError, setEventDateTimeInputError] = useState<string | null>(null);

  useEffect(() => {
    trigger('eventDateTime');
  }, [eventDateTimeInputError, trigger]);

  useEffect(() => {
    const currentValue = getValues('doctor');
    const nextDoctor = defaultDoctor ?? currentDoctor;

    if (isNil(currentValue) && !isNil(nextDoctor)) {
      setValue('doctor', nextDoctor);
    }
  }, [defaultDoctor, currentDoctor, getValues, setValue]);

  const currentFormData = watch();
  const {
    doctor: selectedDoctor,
    patient: selectedPatient,
    insurance: selectedInsurance,
    recurrence: { repeatType } = {},
    reason,
  } = currentFormData;

  const { isCashPay: isOutOfPocket } = selectedPatient ?? {};

  const {
    appointmentTypes,
    durationByType,
    isTelehealthByType,
  } = useEncounterSettings();

  const isTelehealthEnabledForSelectedDoctor =
    selectedDoctor?.isTelehealthEnabled ?? false;

  const isTelehealthVisitType = useCallback((type: string) => (
    isTelehealthEnabledForSelectedDoctor && (isTelehealthByType[type] ?? false)
  ), [isTelehealthEnabledForSelectedDoctor, isTelehealthByType]);

  const {
    data: _insuranceOptions,
    isFetching: insuranceOptionsLoading,
  } = useGetPatientInsurances(selectedPatient?.patientId);

  const {
    suggestions: providerSuggestionData,
    isLoading: isLoadingProviderSuggestions,
  } = useAppointmentReferringProviderSuggestions(
    selectedPatient?.patientId,
    null,
  );

  const referringProviderSuggestions = useMemo(() => (
    providerSuggestionData.map((suggestion) => ({
      ...suggestion,
      onSelect: () => {
        setValue('referringProvider', { providerEntry: suggestion.providerEntry });
      },
    }))
  ), [providerSuggestionData, setValue]);

  const showProviderLoading = referringProviderSuggestions.length === 0
    && isLoadingProviderSuggestions;

  const { mutateAsync: createAppointment } = useCreateAppointment(
    appointmentOptions[appointmentType].key as EventType,
  );

  const onClose = () => {
    _onClose();
    appointmentsEventChannel.emit('onEventCreated', false);
  };

  const onCashPayToggled = (checked: boolean) => {
    if (checked) {
      setValue('insurance', defaultInsurance);
    }
  };

  const onSubmit = async (data: CreateEventFormData) => {
    const isTelehealthType = isTelehealthVisitType(data.typeOfVisitDescription ?? '');

    if (isTelehealthType && !hasTelehealthPatientContact(data.patient)) {
      notify({
        variant: 'error',
        message: 'Telehealth appointments require a patient phone number and email for visit reminders.',
      });
      return;
    }

    try {
      const appointment = await createAppointment(
        serializeAppointmentFormData(data, appointmentType),
      );

      if (!isNil(appointment)) {
        const successMessage = `New ${appointmentOptions[appointmentType].label.toLowerCase()} saved.`;
        handleAppointmentActionDialog(appointment, notify, successMessage);

        mixpanelTrack('appointmentCreated', {
          appointmentType: appointment.typeOfVisitDescription,
          duration: appointment.durationInMinutes,
          canBeRescheduledSooner: appointment.canBeRescheduledSooner,
        });

        if (isAppointment) {
          appointmentsEventChannel.emit('onAppointmentConfirmation', {
            appointment,
            selectedAction: {
              title: 'Confirmation',
              showFormSelect: true,
              allowFormSelect: true,
              showSendModeSelect: true,
              prefillContact: true,
            },
          });
          await onAppointmentCreated?.();
        }
        appointmentsEventChannel.emit('onEventCreated', true, appointment?.encounterId);
        onClose();
      }
    } finally {
      reset(data, { keepIsValid: true });
    }
  };

  useEffect(() => {
    const isValidDateTime = validateDateTimeIntervalValue(getValues('eventDateTime'));
    if (!isValidDateTime) {
      setError('eventDateTime', { message: 'Invalid date or duration' });
    }
  }, []);

  useEffect(() => {
    reset();
  }, [appointmentType]);

  const primaryInsurance = useMemo(() => (
    _insuranceOptions?.find((insurance) => insurance.insuranceSlot === InsuranceSlot.Primary)
  ), [_insuranceOptions]);

  const hasSelectedInsurance = (selectedInsurance?.insuranceId ?? 0) !== 0;

  useEffect(() => {
    if (!isNil(selectedPatient?.patientId) && !isOutOfPocket) {
      setValue('insurance', primaryInsurance);
    }
  }, [selectedPatient, primaryInsurance]);

  useEffect(() => {
    if (hasSelectedInsurance) {
      setValue('isOutOfPocket', false);
    }
  }, [hasSelectedInsurance]);

  useEffect(() => {
    setValue('isOutOfPocket', isOutOfPocket);
  }, [selectedPatient]);

  useEffect(() => {
    if (isAppointment && isNil(defaultDurationInMinutes)) {
      const durationInMinutes = durationByType[
        currentFormData.typeOfVisitDescription
      ] ?? DEFAULT_APPOINTMENT_DURATION;

      setValue('eventDateTime.durationInMinutes', durationInMinutes);
    }
  }, [
    currentFormData.typeOfVisitDescription,
    defaultDurationInMinutes,
    isAppointment,
    setValue,
  ]);

  const appointmentTypeDuration = durationByType[
    currentFormData.typeOfVisitDescription
  ];

  const durationList = useMemo(() => {
    if (!isAppointment || isNil(appointmentTypeDuration)) {
      return undefined;
    }
    return uniq([appointmentTypeDuration, ...DEFAULT_DURATION_LIST]);
  }, [isAppointment, appointmentTypeDuration]);

  const colors = useColorsHashMap();

  const getTypeOfVisitLabel = useCallback((option: string) => {
    const isPlaceholder = isEmptyString(option);
    return (
      <div className="flex items-center gap-1">
        {!isPlaceholder && <div className={clsx('w-2.5 h-2.5 rounded-full', colors[option])} />}
        <span className="max-w-[160px] truncate inline-block">{isPlaceholder ? 'Select' : option}</span>
        <TelehealthVisitTypeIcon
          isTelehealthVisitType={isTelehealthByType[option] ?? false}
          isTelehealthEnabledForProvider={isTelehealthEnabledForSelectedDoctor}
        />
      </div>
    );
  }, [colors, isTelehealthByType, isTelehealthEnabledForSelectedDoctor]);

  const shouldAutoFocusPatient = isAppointment && isNil(selectedPatient?.patientId);
  const shouldAutoFocusDoctor = !isAppointment && isNil(selectedDoctor?.userId);
  const ref = useFormAutoFocus<HTMLInputElement>({
    enabled: shouldAutoFocusPatient || shouldAutoFocusDoctor,
  }, [
    appointmentType,
    selectedPatient?.patientId ?? 0,
    selectedDoctor?.userId ?? 0,
  ]);

  return (
    <Dialog
      open={open}
      title={(
        <Title
          active={appointmentType}
          onChange={setAppointmentType}
        />
      )}
      onClose={onClose}
      confirmText="Create"
      className="!w-[48rem] justify-between nw-create-appointment-dialog overflow-x-auto populate-form !space-y-0"
      maxWidth="lg"
      disableEscapeKeyDown
      disableBackdropClick
      dialogRef={dialogRef}
      autoFocusOptions={{ enabled: false }}
    >
      <FormProvider {...formProps}>
        <div className="relative flex flex-col gap-form-row flex-1 overflow-y-auto -m-5 p-5 pt-2">
          {isAppointment && (
            <div className="flex flex-col w-full gap-form-row">
              <Controller
                control={control}
                name="patient"
                rules={{
                  required: 'Select patient',
                  validate: validatePatient,
                }}
                render={({ field: { onChange, ...field }, fieldState: { invalid } }) => (
                  <PatientSelect
                    {...field}
                    value={field.value as CreateEventFormData['patient']}
                    inputRef={ref}
                    className="!mb-0"
                    label="Patient"
                    id="patient-lookup"
                    error={invalid}
                    required
                    readonly={isSubmitting}
                    placeholder="Enter patient name, phone or date of birth"
                    allowDefault
                    onAddPatient={(phoneNumber: string) => {
                      patientEventChannel.emit('onCreatePatientDialog', {
                        defaultPhoneNumber: /^\d{10,}$/.test(phoneNumber) ? phoneNumber : '',
                        onPatientSelect: onChange,
                        dialogHeight: dialogRef?.current?.offsetHeight,
                      });
                    }}
                    disabled={isSubmitting}
                    containerRef={dialogRef}
                    onChange={onChange}
                  />
                )}
              />
              {!insuranceOptionsLoading
                && !isNil(selectedPatient?.patientId)
                && (
                  <div className="w-full flex items-end relative gap-form-field">
                    <Controller
                      name="insurance"
                      control={control}
                      render={({ field: { value, ...field } }) => (
                        <Wrapper
                          id="patient-insurance"
                          label="Insurance"
                          className="text-gray-500 text-xs !w-[calc(100%-216px)]"
                          containerClassName="w-full items-center gap-1"
                        >
                          <PatientInsuranceSelect
                            {...field}
                            patientId={selectedPatient?.patientId ?? 0}
                            className="!w-[calc(100%-84px)]"
                            insuranceId={(value as InsuranceDTO)?.insuranceId ?? 0}
                            disabled={isNil(selectedPatient?.patientId)}
                            containerRef={dialogRef}
                          />
                        </Wrapper>
                      )}
                    />
                    <Divider
                      orientation="vertical"
                      className="!text-xs font-semibold text-stone-400"
                    >
                      OR
                    </Divider>
                    <Controller
                      name="isOutOfPocket"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <ButtonCheckbox
                          className="!p-0 -top-1"
                          classes={{ button: '!pl-3' }}
                          label="Out-of-pocket"
                          value={(value ?? isOutOfPocket ?? '').toString()}
                          checked={Boolean(value) ?? isOutOfPocket ?? false}
                          onChange={(event, checked: boolean) => {
                            onCashPayToggled(checked);
                            onChange(checked);
                          }}
                          disabled={(!isAppointment && !isAvailability)}
                        />
                      )}
                    />
                  </div>
                )}
            </div>
          )}
          <div
            className={clsx('relative gap-x-form-field gap-y-form-row', {
              'grid grid-cols-3': isAppointment,
              'grid grid-cols-2': isAvailability,
              flex: isTimeBlock,
            })}
          >
            <Controller
              name="doctor"
              control={control}
              rules={{ required: 'Select doctor' }}
              render={({ field, fieldState: { invalid } }) => (
                <Wrapper
                  id="doctor"
                  required
                  className={clsx('min-w-0 text-gray-500 text-xs', {
                    'flex-1': isTimeBlock,
                  })}
                  label="Doctor"
                  error={invalid}
                  showErrorText={false}
                >
                  <ProviderLookup
                    {...field}
                    inputRef={!isAppointment ? ref : undefined}
                  />
                </Wrapper>
              )}
            />
            {(isAppointment || isAvailability) && (
              <Controller
                name="serviceFacility"
                control={control}
                rules={{ required: 'Select facility' }}
                render={({ field }) => (
                  <ServiceFacilitySelect
                    {...field}
                    value={field?.value as ServiceFacilityDTO}
                    label="Facility"
                    className="min-w-0"
                    required
                  />
                )}
              />
            )}
            {isAppointment && (
              <Controller
                name="typeOfVisitDescription"
                control={control}
                rules={{ required: 'Select type of visit' }}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                  <Select<string>
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    id="type-of-visit"
                    className="min-w-0 text-gray-500 text-xs"
                    label="Type of Visit"
                    placeholder="Select"
                    options={appointmentTypes}
                    value={value}
                    getLabel={(option: string) => getTypeOfVisitLabel(option)}
                    renderValue={() => getTypeOfVisitLabel(value as string)}
                    getValue={(option: string) => option}
                    onChange={(option) => {
                      if (isEmptyString(reason) || value === reason) {
                        setValue('reason', option as string);
                        trigger('reason');
                      }
                      onChange(option as string);
                    }}
                    required
                  />
                )}
              />
            )}
          </div>
          <Controller
            name="eventDateTime"
            control={control}
            rules={{
              required: 'Select date and time',
              validate: {
                inputError: () => isNil(eventDateTimeInputError) || eventDateTimeInputError,
                invalidValue: (value) => (
                  validateDateTimeIntervalValue(value)
                  || 'Invalid date or duration'
                ),
                minAppointmentDate: (value) => (
                  validateMinAppointmentDateTime(value)
                  || MIN_APPOINTMENT_DATE_ERROR_MESSAGE
                ),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                id="appointment-date-range"
                label="Date & Time"
                error={!isNil(error)}
                errorText={error?.message}
                showErrorText={false}
                required
                className="w-full text-gray-500 text-xs relative z-10"
              >
                <DateTimeIntervalPicker
                  {...field}
                  value={field?.value}
                  timezone={userInfo?.organization?.timeZoneId ?? undefined}
                  durationList={durationList}
                  suggestedDurationInMinutes={appointmentTypeDuration}
                  onInputErrorChange={setEventDateTimeInputError}
                  variant={isAppointment ? 'durationPicker' : 'timepicker'}
                />
              </Wrapper>
            )}
          />
          {!isAvailability && (
            <Controller
              name="reason"
              control={control}
              rules={{ required: `Enter reason for ${!isAppointment ? 'time block' : 'visit'}` }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  className="text-gray-500 text-xs"
                  id="reason-for-visit"
                  label={`Reason for ${!isAppointment ? 'time block' : 'visit'}`}
                  required
                  error={invalid}
                  showErrorText={false}
                />
              )}
            />
          )}
          {isAppointment && (
            <Controller
              name="referringProvider"
              control={control}
              render={({ field }) => (
                <Wrapper
                  id="referring-provider"
                  label={isNil(field.value) ? undefined : 'Referring Provider'}
                  className="font-semibold relative text-xs text-gray-500"
                >
                  <ReferringProvider
                    {...field}
                    value={field?.value?.providerEntry}
                    onChange={(provider?: NPISearchItemDTO | null) => {
                      field.onChange(isNil(provider) ? null : {
                        providerEntry: mapNPISearchItemToProviderEntry(provider),
                        firstSeenOn: field.value?.firstSeenOn ?? null,
                        lastSeenOn: field.value?.lastSeenOn ?? null,
                      });
                    }}
                    suggestions={referringProviderSuggestions}
                    loading={showProviderLoading}
                    dateSeen={{
                      firstSeenOn: field.value?.firstSeenOn ?? null,
                      lastSeenOn: field.value?.lastSeenOn ?? null,
                      onChange: ({ firstSeenOn, lastSeenOn }) => {
                        field.onChange({
                          ...(field.value ?? {}),
                          firstSeenOn: firstSeenOn ?? null,
                          lastSeenOn: lastSeenOn ?? null,
                        });
                      },
                    }}
                    dialogHeight={dialogRef.current?.offsetHeight}
                    showContactButton={false}
                    variant="compactBorder"
                  />
                </Wrapper>
              )}
            />
          )}
          {isAppointment && (
            <Controller
              name="canBeRescheduledSooner"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  id="can-be-scheduled-sooner"
                  checked={Boolean(field.value)}
                  value={(field?.value ?? '').toString()}
                  label="Patient would rather be seen sooner if schedule permits"
                />
              )}
            />
          )}
          {(isAvailability || isTimeBlock) && (
            <div className="mt-4">
              <RecurrenceCheckbox />
              {!isNil(repeatType) && (
                <>
                  <RecurrenceFields />
                  <EndsOnConditionFields />
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-row-reverse justify-between mt-4">
          <DisabledFormTooltip>
            <Button
              className="!py-1.5 ml-3"
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              Create
            </Button>
          </DisabledFormTooltip>
          <Button
            className="nw-cancel-appointment !py-1.5 ml-3"
            variant="outlined"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
